// BETA 19.85 — Replay launcher panel. Dylan: "maybe make a button on screen
// that has a live list of past recent runs. max like 100 or something and
// you can chose it and it plays it from the jump with all items all buffs
// all shriines. at the time of death so that enemies are the same streangth
// and with death not restarting the atch so you can run test."
//
// Floating button bottom-LEFT (DevPanel owns bottom-right). Click → opens
// scrollable list of last 100 live_play runs from Supabase. Click any row →
// calls window.__replayRun(id) which spawns a fresh game with that loadout
// pre-applied (weapons, items, percs, shrines, time-warp to time of death).
//
// Auto-respawn on bot death: when bot dies in a replay session, this panel
// re-fires the last replay so testing runs for hours without manual restart.
// Tracks death count in a small badge.

import { useState, useEffect, useRef } from 'react';
import { isDevUser } from '../game/devUsers';

interface Props { username: string; currentScreen: string; }

// BETA 20.47 — Glue this dev launcher to the 16:9 menu box (same letterbox math
// as Home.tsx's dev buttons + ClassroomMenu's ● BETA badge) so it stops drifting
// into the letterbox bars in non-16:9 aspect ratios. Anchored to the box's
// bottom-left corner; at exactly 16:9 both bars are 0 so positions are unchanged.
const _stageLeft = (px: number) => `calc((100vw - min(100vw, 100dvh * 16 / 9)) / 2 + ${px}px)`;
const _stageBottom = (px: number) => `calc((100dvh - min(100dvh, 100vw * 9 / 16)) / 2 + ${px}px)`;

// Schema matches RunSummary returned by window.__listRecentRuns
type RunSummary = {
  id: string;
  createdAt: string;
  character: string;
  betaVersion: string;
  survivalSec: number;
  kills: number;
  level: number;
  damage: number;
  weapons: string[];
  itemCount: number;
  percCount: number;
  shrineCount: number;
};

type Win = Window & {
  __listRecentRuns?: (opts?: { limit?: number; character?: string }) => Promise<RunSummary[]>;
  __replayRun?: (id: string, opts?: { startAtTimeSec?: number; playerLevel?: number }) => Promise<boolean>;
  __replayLastRun?: () => Promise<boolean>;
  __getEnginePhase?: () => string | null;
  // BETA 19.86 — Set when a replay session is active. Engine reads this in
  // the death-handling branch and falls through to the lab-style respawn
  // (full HP, keep build, clear nearby enemies, +1 death counter) instead
  // of going to gameover/death_replay. Lets a replay loop forever for
  // multi-hour testing without the bot's autoRestart 'r' key path firing.
  __replaySessionActive?: boolean;
};

const C = {
  bg:        'rgba(28, 38, 28, 0.97)',
  border:    'rgba(200, 195, 185, 0.40)',
  text:      'rgba(235, 230, 220, 0.92)',
  dim:       'rgba(210, 205, 195, 0.55)',
  accent:    'rgba(140, 230, 255, 0.95)', // cyan to match 19.68 focus box
  danger:    'rgba(255, 100, 80, 0.85)',
  rowHover:  'rgba(120, 200, 255, 0.10)',
};

export default function ReplayLauncherPanel({ username, currentScreen }: Props) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [charFilter, setCharFilter] = useState<string>('');
  const [deathCount, setDeathCount] = useState(0);
  const [lastReplayId, setLastReplayId] = useState<string | null>(null);
  const lastReplayRef = useRef<string | null>(null);

  const w = window as Win;

  // Load runs on open (and refresh button)
  const loadRuns = async () => {
    setLoading(true);
    try {
      const list = await w.__listRecentRuns?.({ limit: 100, character: charFilter || undefined }) ?? [];
      setRuns(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, charFilter]);

  // BETA 19.86 — Auto-respawn watcher SIMPLIFIED. The engine now handles
  // death itself via the lab-style respawn path (full HP, keep build,
  // clear enemies, never enters gameover phase) when __replaySessionActive
  // is true. This watcher just polls the lab-death log to keep our visible
  // death counter in sync. No more re-applying the build (which was racy
  // with the bot's auto-restart 'r' key path that wiped the loadout).
  useEffect(() => {
    if (!lastReplayId) return;
    let lastSeenDeathCount = 0;
    const interval = setInterval(async () => {
      try {
        const labMod = await import('@/game/weaponLab');
        const deaths = labMod.getLabDeaths?.() ?? [];
        if (deaths.length !== lastSeenDeathCount) {
          lastSeenDeathCount = deaths.length;
          setDeathCount(deaths.length);
        }
      } catch { /* noop */ }
    }, 500);
    return () => clearInterval(interval);
  }, [lastReplayId]);

  const fireReplay = async (id: string) => {
    setLastReplayId(id);
    lastReplayRef.current = id;
    setDeathCount(0);
    setOpen(false);
    // BETA 19.86 — Flip the engine flag BEFORE firing the replay so the
    // first death (which might happen seconds after spawn during the
    // first enemy onslaught) is handled by the lab-respawn path, not
    // gameover.
    w.__replaySessionActive = true;
    await w.__replayRun?.(id);
  };

  const stopAutoRespawn = () => {
    setLastReplayId(null);
    lastReplayRef.current = null;
    w.__replaySessionActive = false;
  };

  if (!isDevUser(username)) return null;
  if (currentScreen !== 'game' && currentScreen !== 'menu') return null;
  // BETA 20.53 — Dylan: the Replays launcher must NOT sit on screen during a
  // normal game (it was covering HUD). Show it on the menu (where you launch
  // replays), OR mid-game ONLY while a replay session is actually running
  // (lastReplayId set) so you can still see/stop it. Hidden during normal play.
  if (currentScreen === 'game' && !lastReplayId) return null;

  // Float button bottom-LEFT (DevPanel owns bottom-right)
  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: _stageBottom(172), left: _stageLeft(12), zIndex: 9000,
          padding: '8px 14px', borderRadius: 8,
          background: open ? 'rgba(60, 80, 60, 0.95)' : 'rgba(28, 38, 28, 0.85)',
          color: C.accent, border: `1px solid ${C.border}`,
          fontSize: 12, fontFamily: 'monospace', cursor: 'pointer',
          backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        }}
        title="Replay launcher (dev only)"
      >
        🔁 Replays
        {lastReplayId && <span style={{ marginLeft: 8, color: C.danger }}>☠ {deathCount}</span>}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed', bottom: _stageBottom(212), left: _stageLeft(12), zIndex: 9001,
            width: 480, maxHeight: '70vh',
            background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: 12, color: C.text,
            fontSize: 11, fontFamily: 'monospace',
            display: 'flex', flexDirection: 'column', gap: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ color: C.accent }}>Past Runs</strong>
            <span style={{ color: C.dim }}>({runs.length})</span>
            <span style={{ flex: 1 }} />
            <input
              placeholder="filter char"
              value={charFilter}
              onChange={e => setCharFilter(e.target.value)}
              style={{
                width: 90, background: 'rgba(0,0,0,0.4)', border: `1px solid ${C.border}`,
                color: C.text, padding: '3px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 11,
              }}
            />
            <button
              onClick={loadRuns}
              disabled={loading}
              style={{ padding: '4px 8px', background: 'rgba(40,60,40,0.8)', color: C.text, border: `1px solid ${C.border}`, borderRadius: 4, cursor: 'pointer' }}
            >{loading ? '...' : '↻'}</button>
            {lastReplayId && (
              <button
                onClick={stopAutoRespawn}
                style={{ padding: '4px 8px', background: 'rgba(80,40,40,0.8)', color: C.danger, border: `1px solid ${C.danger}`, borderRadius: 4, cursor: 'pointer' }}
                title="Stop auto-respawn on death"
              >stop</button>
            )}
          </div>

          {lastReplayId && (
            <div style={{ color: C.dim, fontSize: 10, padding: '2px 4px', borderTop: `1px dashed ${C.border}`, borderBottom: `1px dashed ${C.border}` }}>
              auto-respawn ON · deaths: <strong style={{ color: C.danger }}>{deathCount}</strong> · pinned to {lastReplayId.slice(0, 8)}
            </div>
          )}

          <div style={{ overflowY: 'auto', maxHeight: '52vh', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {runs.length === 0 && !loading && (
              <div style={{ color: C.dim, padding: 20, textAlign: 'center' }}>No runs (yet). Play a game, then refresh.</div>
            )}
            {runs.map(r => {
              const pinned = r.id === lastReplayId;
              return (
                <div
                  key={r.id}
                  onClick={() => fireReplay(r.id)}
                  style={{
                    padding: '6px 8px', borderRadius: 4, cursor: 'pointer',
                    background: pinned ? C.rowHover : 'transparent',
                    border: pinned ? `1px solid ${C.accent}` : '1px solid transparent',
                    display: 'flex', flexDirection: 'column', gap: 2,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.rowHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = pinned ? C.rowHover : 'transparent'; }}
                  title={`Click to replay this run (loadout active from time-of-death)`}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <strong style={{ color: C.accent, minWidth: 60 }}>{r.character}</strong>
                    <span style={{ color: C.text }}>L{r.level}</span>
                    <span style={{ color: C.dim }}>·</span>
                    <span style={{ color: C.text }}>{Math.round(r.survivalSec)}s</span>
                    <span style={{ color: C.dim }}>·</span>
                    <span style={{ color: C.text }}>{r.kills.toLocaleString()} kills</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: C.dim, fontSize: 10 }}>{r.betaVersion}</span>
                  </div>
                  <div style={{ color: C.dim, fontSize: 10 }}>
                    {r.weapons.length} wpns · {r.itemCount} items · {r.percCount} percs · {r.shrineCount} shrines
                    <span style={{ float: 'right' }}>{r.createdAt.replace('T', ' ').slice(5, 16)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
